// src/components/personal/sections/OrdersSection/hooks/useOrderFilters.ts
import { useState, useCallback, useMemo } from 'react';
import { Order, OrderFilters } from '../types/types';
import { 
  filterOrders, 
  isFilteringActive, 
  getEmptyFilters 
} from '../utils/orderFilters';

interface UseOrderFiltersProps {
  orders: Order[];
  onFiltersChange?: (filters: OrderFilters) => void;
}

interface UseOrderFiltersReturn {
  // Состояние фильтров
  filters: OrderFilters;
  filteredOrders: Order[];
  isFiltering: boolean;
  showFilters: boolean;
  
  // Методы управления фильтрами
  updateFilters: (newFilters: Partial<OrderFilters>) => void;
  resetFilters: () => void;
  toggleFilters: () => void;
}

export const useOrderFilters = ({
  orders,
  onFiltersChange
}: UseOrderFiltersProps): UseOrderFiltersReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние фильтров
  const [filters, setFilters] = useState<OrderFilters>(getEmptyFilters());
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Определяем, активны ли фильтры
  const isFiltering = useMemo(() => {
    // Используем searchQuery = '' так как он обрабатывается в useOrderSearch
    return isFilteringActive(filters, '');
  }, [filters]);
  
  // Применение фильтров к списку заказов
  const filteredOrders = useMemo(() => {
    if (orders.length === 0) {
      return [];
    }
    
    if (!isFiltering) {
      return orders;
    }
    
    if (DEBUG) console.log(`Фильтруем ${orders.length} заказов по фильтрам:`, filters);
    
    // Используем searchQuery = '' так как он обрабатывается в useOrderSearch
    const result = filterOrders(orders, filters, '');
    
    if (DEBUG) console.log(`После фильтрации осталось ${result.length} заказов`);
    
    return result;
  }, [orders, filters, isFiltering, DEBUG]);
  
  // Метод обновления фильтров
  const updateFilters = useCallback((newFilters: Partial<OrderFilters>) => {
    setFilters(current => {
      const updated = { ...current, ...newFilters };
      
      if (onFiltersChange) {
        onFiltersChange(updated);
      }
      
      return updated;
    });
  }, [onFiltersChange]);
  
  // Сброс фильтров
  const resetFilters = useCallback(() => {
    const emptyFilters = getEmptyFilters();
    setFilters(emptyFilters);
    
    if (onFiltersChange) {
      onFiltersChange(emptyFilters);
    }
  }, [onFiltersChange]);
  
  // Переключение отображения панели фильтров
  const toggleFilters = useCallback(() => {
    setShowFilters(current => !current);
  }, []);
  
  return {
    filters,
    filteredOrders,
    isFiltering,
    showFilters,
    updateFilters,
    resetFilters,
    toggleFilters
  };
};